import { useTranslation } from "react-i18next";
import type { S } from "@/shared/api/client";
import i18n from "@/shared/i18n";
import { useCountry } from "@/shared/lib/session";
import type { Tone } from "@/shared/ui/Badge";

export type Emp = S["HrEmployee"];

type Named = { first_name_ar: string; last_name_ar?: string; first_name_en?: string; last_name_en?: string };

/** An employee's name: in English when the app is in English and the file has one, as with products and accounts. */
export const empName = (e: Named) => {
  const en = `${e.first_name_en ?? ""} ${e.last_name_en ?? ""}`.trim();
  return i18n.language === "en" && en ? en : `${e.first_name_ar} ${e.last_name_ar ?? ""}`.trim();
};

/** Whether an employee's name, in either language, has q in it (a picker's search). */
export const empHas = (e: Named, q: string) => [e.first_name_ar, e.last_name_ar, e.first_name_en, e.last_name_en].join(" ").toLowerCase().includes(q.toLowerCase());
export const empOption = (e: Emp) => ({ id: e.id, label: empName(e), sub: e.employee_no });

export const dayTone = (s: string): Tone => ({ present: "green", absent: "red", leave: "blue", trip: "blue", incomplete: "gold", holiday: "neutral", weekend: "neutral", off: "neutral" } as Record<string, Tone>)[s] ?? "neutral";

/** How employment ended, in words. Outside Saudi Arabia, article 80 stands for dismissal for gross misconduct. */
export function useTerminationLabel() {
  const { t } = useTranslation();
  const country = useCountry();
  return (type: string) => t(country !== "SA" && type === "article_80" ? "hr.terminationTypes.misconduct" : `hr.terminationTypes.${type}`);
}
